import {  html, LitElement, nothing, unsafeCSS, type CSSResultGroup } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { consume } from '@lit/context'
import { navbarContext, type NavbarPart } from '../context'
import { $t, localized } from '@/navbar/i18n'
import type { MenuItem } from '@/navbar/navbar.types'
import { ifDefined } from 'lit/directives/if-defined.js'

// Styles:
import styles from './eco-suite-navbar-account-menu.scss?inline'

// Child components:
import '../button/eco-suite-navbar-button.component'
import '../menu/eco-suite-navbar-menu.component'
import '../menu-item/eco-suite-navbar-menu-item.component'
import '../menu-divider/eco-suite-navbar-menu-divider.component'
import '../avatar/eco-suite-navbar-avatar.component'

/**
 * Account menu surface for the navbar. Renders an avatar button trigger (when
 * `userName` is set and the user is not a guest), a username header, divider,
 * and a list of menu items with an optional logout entry.
 *
 * When `isGuest` is `true` the menu always renders in guest mode regardless of
 * `userName` — the avatar button, header, and divider are suppressed and the
 * label reads "Guest".
 *
 * @internal
 */
@localized()
@customElement('eco-suite-navbar-account-menu')
export class EcoSuiteNavbarAccountMenu extends LitElement {
  static readonly shadowRootOptions = { ...LitElement.shadowRootOptions, delegatesFocus: true }
  static readonly styles: CSSResultGroup = [unsafeCSS(styles)]

  @property()
  userName?: string

  @property({ attribute: false })
  menuItems?: MenuItem[]

  @property({ type: Boolean })
  isGuest = false

  @property({ attribute: false })
  onLogout?: (e: Event) => void

  @property()
  logoutHref?: string

  @consume({ context: navbarContext, subscribe: true })
  navbarContext?: NavbarPart

  @state()
  private menuOpen = false

  render() {
    const showUserBlock = !!this.userName && !this.isGuest

    return html`
      <eco-suite-navbar-menu
        .label=${this.isGuest ? $t('navbar.guest') : $t('navbar.yourAccount')}
        .icon=${showUserBlock ? undefined : 'person'}
        .hideMenuHeader=${showUserBlock}
        data-qa-id="account-menu"
        @eco-after-show=${() => this.menuOpen = true}
        @eco-after-hide=${() => this.menuOpen = false}
      >
        ${showUserBlock ? html`
          <eco-suite-navbar-button
            slot="button"
            .label=${this.userName}
            .tooltip=${$t('navbar.yourAccount')}
            .isActive=${this.menuOpen}
            .isAccountMenu=${true}
            .hasMenu=${true}
            data-qa-id="account-menu-button"
          >
            <eco-suite-navbar-avatar
              slot="icon"
              .userName=${this.userName}
              .isInButton=${true}
            ></eco-suite-navbar-avatar>
          </eco-suite-navbar-button>
          <div role="none" class="EcoSuiteNavbarAccountMenu__header">
            <eco-suite-navbar-avatar
              .userName=${this.userName}
            ></eco-suite-navbar-avatar>
            <span>${this.userName}</span>
          </div>
          <eco-suite-navbar-menu-divider></eco-suite-navbar-menu-divider>
        ` : nothing}
        ${this.menuItems?.map((item) => html`
          <eco-suite-navbar-menu-item
            .icon=${item.icon}
            .label=${item.label}
            .href=${item.href}
            .onClick=${item.onClick}
            .isCurrent=${item.isCurrent}
            .target=${item.target}
            .isExternal=${item.isExternal}
            data-qa-id=${ifDefined(item.dataQaId)}
          ></eco-suite-navbar-menu-item>
        `)}
        <slot></slot>
        ${
          (this.onLogout || this.logoutHref) ? html`
            <eco-suite-navbar-menu-item
              icon="logout"
              .label=${$t('navbar.logOut')}
              .href=${this.logoutHref}
              @click=${this.onLogout}
            ></eco-suite-navbar-menu-item>
          ` : nothing
        }
      </eco-suite-navbar-menu>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'eco-suite-navbar-account-menu': EcoSuiteNavbarAccountMenu
  }
}
